<template>
  <v-dialog v-model="dialog" max-width="820" persistent scrollable>
    <v-card class="serial-search-dialog">
      <div class="dialog-header">
        <div class="dialog-title-wrap">
          <div class="dialog-icon"><v-icon color="primary">mdi-feature-search-outline</v-icon></div>
          <div>
            <div class="dialog-title">Serial Search</div>
            <div class="dialog-subtitle">Find a serial number and review its complete history</div>
          </div>
        </div>
        <v-btn icon small @click.stop.prevent="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="dialog-body">
        <div class="search-panel">
          <div class="search-input-wrap">
            <v-text-field
              @focus="$event.target.select()"
              label="Enter serial or IMEI number"
              hide-details
              outlined
              prepend-inner-icon="search"
              clearable
              dense
              @keyup.enter="loadImeiNumber()"
              v-model.trim="returnImeiNumber"
              ref="returnImeiNumber"
            />
          </div>
            <v-btn
              color="primary"
              rounded
              depressed
              class="search-button"
              :loading="textLoading"
              @click="loadImeiNumber()"
            ><v-icon left small>mdi-magnify</v-icon>Search</v-btn>
        </div>

        <div v-if="product" class="result-wrap">
          <div class="product-card">
            <div class="product-main">
              <div class="product-avatar"><v-icon color="primary">mdi-package-variant-closed</v-icon></div>
              <div>
                <div class="eyebrow">PRODUCT</div>
                <div class="product-title">{{ product.Product.name }}</div>
                <div class="serial-number"><v-icon x-small class="mr-1">mdi-barcode</v-icon>{{ product.imeiNumber || returnImeiNumber }}</div>
              </div>
            </div>
            <div class="product-meta">
              <div class="meta-item"><span>Product ID</span><strong>#{{ product.Product.localId }}</strong></div>
              <div class="product-action-stack">
                <v-btn v-if="checkRightStatus(40)" small rounded depressed class="edit-product-button" @click="openProductEditor">
                  <v-icon small left>mdi-pencil-outline</v-icon>Edit Product
                </v-btn>
                <v-chip small :class="['result-status', 'result-status-' + product.status]">
                  <span class="status-dot"></span>{{ product.status }}
                </v-chip>
              </div>
              <v-chip
                v-if="product.Product.deletedAt"
                small
                outlined color="red" class="ml-2"
                >Deleted
                {{ product.Product.deletedAt | moment("MM/DD/YYYY") }}
              </v-chip>
            </div>
          </div>

          <div v-if="hasPurchaseNameDifference" class="purchase-name-card">
            <div class="purchase-name-heading">
              <div class="purchase-name-icon">
                <v-icon color="amber darken-2">mdi-history</v-icon>
              </div>
              <div>
                <div class="eyebrow">NAME AT TIME OF PURCHASE</div>
                <div class="purchase-name-title">{{ purchaseDetail.name }}</div>
                <div class="dialog-subtitle">
                  Recorded on
                  {{ purchaseDate | moment("MM/DD/YYYY LT") }}
                  <span v-if="purchaseDetail.Purchase">
                    · Purchase #{{ purchaseDetail.Purchase.localId }}
                  </span>
                </div>
              </div>
            </div>

            <v-alert dense text type="warning" class="name-change-alert mb-0">
              The name recorded when this serial was purchased is different
              from the product name currently linked to it.
            </v-alert>

            <div v-if="relevantProductNameHistory.length" class="name-audit-wrap">
              <div class="name-audit-title">Recorded product name changes</div>
              <div
                v-for="history in relevantProductNameHistory"
                :key="history.id"
                class="name-audit-row"
              >
                <div>{{ history.changesSummary }}</div>
                <div class="dialog-subtitle">
                  {{ history.createdAt | moment("MM/DD/YYYY LT") }} ·
                  {{ history.UserSnapshotName || (history.User && history.User.name) || "System" }}
                </div>
              </div>
            </div>
            <div v-else class="name-audit-wrap">
              <div class="name-audit-title">No matching name-change audit found</div>
              <div class="dialog-subtitle">
                There is no audit entry recording a change from
                “{{ purchaseDetail.name.trim() }}” to
                “{{ product.Product.name }}”. The mismatch may predate product
                name auditing or may have resulted from a product-association
                change rather than a name edit.
              </div>
            </div>
          </div>

          <div v-if="productLinkMismatch" class="product-link-card">
            <div>
              <div class="eyebrow">PRODUCT LINK NEEDS CORRECTION</div>
              <div class="product-link-title">
                Serial Vault currently points to Product #{{ product.Product.localId }},
                but its active purchase record points to Product #{{ purchaseDetail.Product.localId }}.
              </div>
              <div class="dialog-subtitle mt-1">
                Restore the serial to {{ purchaseDetail.Product.name }} using the active purchase record.
              </div>
            </div>
            <v-btn
              small
              rounded
              depressed
              color="warning"
              :loading="correctingProductLink"
              @click="correctProductLink"
            >
              <v-icon small left>mdi-link-variant</v-icon>
              Restore Product #{{ purchaseDetail.Product.localId }}
            </v-btn>
          </div>

          <div class="history-header">
            <div><div class="section-title">Activity History</div><div class="dialog-subtitle">{{ InvoiceDetail.length }} recorded event{{ InvoiceDetail.length === 1 ? "" : "s" }}</div></div>
          </div>
          <div class="history-table-wrap">
          <v-data-table
            :loading="textLoading"
            :mobile-breakpoint="0"
            :headers="headers"
            sort-by="createdAt"
            :sort-desc="true"
            :itemsPerPage="InvoiceDetail.length"
            hide-default-footer
            :items="InvoiceDetail"
            class="history-table"
          >
            <template v-slot:item="{ item }">
              <tr class="history-row">
                <td
                  class="text-left pointer history-date"
                  @click="view(item)"
                >
                  <div>{{ item.createdAt | moment("MM/DD/YYYY") }}</div>
                  <div class="cell-subtitle">{{ item.createdAt | moment("LT") }}</div>
                </td>

                <td>
                  <div class="history-message"><span :class="['log-dot', logClass(item)]"></span>{{ item.message }}</div>
                  <div
                    v-if="canViewReference(item)"
                    class="history-reference pointer"
                    role="button"
                    tabindex="0"
                    @click.stop="view(item)"
                    @keydown.enter.stop="view(item)"
                  >
                    {{ item.referenceLabel }} #{{ item.displayReferenceNumber }}
                    <v-icon x-small color="primary">mdi-open-in-new</v-icon>
                  </div>
                </td>
                <td>
                  <v-chip
                    small
                    :class="['activity-chip', actionMeta(item).className]"
                  >
                    <v-icon x-small left>{{ actionMeta(item).icon }}</v-icon>
                    {{ actionMeta(item).label }}
                  </v-chip>
                </td>
                <td>
                  <div class="user-cell"><v-icon small class="mr-1" color="grey">mdi-account-circle-outline</v-icon>{{ item.User ? item.User.name : "System" }}</div>
                </td>
                <td class="text-right">
                  <v-btn
                    v-if="item.type === 'purchase' || item.type === 'returnToVendor'"
                    small
                    text
                    icon
                    title="Edit purchase"
                    @click="openPurchase(item)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="item.type === 'invoice' || item.type === 'refundInvoice' || item.type === 'refund'"
                    small
                    text
                    icon
                    title="Edit invoice"
                    @click="openInvoice(item)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template v-slot:no-data><div class="empty-history">No activity has been recorded for this serial number.</div></template>
          </v-data-table>
          </div>
        </div>

        <div v-else-if="!textLoading" class="search-empty">
          <div class="empty-icon"><v-icon size="42" color="blue-grey lighten-2">mdi-barcode-scan</v-icon></div>
          <div class="empty-title">Search for a serial number</div>
          <div class="dialog-subtitle">Product status, purchase, invoice, and adjustment history will appear here.</div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-actions">
        <div v-if="checkRightStatus(48)">
          <v-btn
            v-if="product"
            :disabled="!checkRightStatus(40)"
            :color="btnColor"
            rounded
            depressed
            @click="submit"
            ><v-icon left small>{{ product.status === 'available' ? 'mdi-cart-arrow-right' : 'mdi-restore' }}</v-icon>{{ label }}</v-btn
          >
        </div>

        <v-spacer> </v-spacer>
        <v-btn text rounded color="grey darken-1" @click="close">Close</v-btn>
      </v-card-actions>

      <v-dialog v-model="productEditorOpen" max-width="620" persistent>
        <v-card class="product-editor-dialog">
          <div class="product-editor-header">
            <div class="dialog-title-wrap">
              <div class="dialog-icon product-editor-icon"><v-icon color="primary">mdi-package-variant-closed-edit</v-icon></div>
              <div>
                <div class="dialog-title">Change Serial Product</div>
                <div class="dialog-subtitle">Correct the product linked to this serial number</div>
              </div>
            </div>
            <v-btn icon small @click="productEditorOpen = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <v-card-text class="product-editor-body">
            <div class="serial-editor-summary">
              <div class="summary-icon"><v-icon color="primary">mdi-barcode-scan</v-icon></div>
              <div>
                <div class="eyebrow">SERIAL / IMEI</div>
                <div class="serial-editor-number">{{ returnImeiNumber }}</div>
                <div class="dialog-subtitle">Currently linked to Product #{{ product.Product.localId }} · {{ product.Product.name }}</div>
              </div>
            </div>
            <div class="product-editor-field">
              <div class="field-label">Select the correct product</div>
            <v-autocomplete
              v-model="selectedTargetProduct"
              :items="serializedProducts"
              :loading="loadingProducts"
              item-text="name"
              return-object
              outlined
              rounded
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              placeholder="Search serialized products"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>Product #{{ item.localId }} · Balance {{ item.balance }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            </div>
            <div class="inventory-note">
              <v-icon small color="blue darken-1">mdi-information-outline</v-icon>
              <span>If this serial is in stock, its inventory balance will move automatically to the selected product.</span>
            </div>
          </v-card-text>
          <v-card-actions class="product-editor-actions">
            <v-spacer />
            <v-btn text rounded color="grey darken-1" @click="productEditorOpen = false">Cancel</v-btn>
            <v-btn
              color="primary"
              rounded
              depressed
              :disabled="!selectedTargetProduct || Number(selectedTargetProduct.id) === Number(product.Product.id)"
              :loading="correctingProductLink"
              @click="changeProduct"
            ><v-icon small left>mdi-check</v-icon>Change Product</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import purchaseService from "@/modules/Inventory/Purchase/service";
import productService from "@/modules/Product/service";
import Hashids from "hashids";
const hashids = new Hashids();
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      product: null,
      btnColor: "info",
      label: "Restock",
      InvoiceDetail: [],
      productStatus: null,
      textLoading: false,
      dialog: false,
      returnImeiNumber: null,
      purchaseDetail: null,
      productNameHistory: [],
      correctingProductLink: false,
      productEditorOpen: false,
      selectedTargetProduct: null,
      serializedProducts: [],
      loadingProducts: false,
      headers: [
        {
          text: "UPDATED AT",
          align: "left",
          value: "date",
          sortable: true,
        },

        {
          text: "MESSAGE",
          align: "center",
          value: "qty",
          sortable: true,
        },
        {
          text: "TYPE",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "UPDATED BY",
          align: "left",
          value: "User.name",
          sortable: true,
        },
        {
          text: "",
          align: "right",
          value: "action",
          sortable: false,
        },
      ],
    };
  },
  watch: {
    openSerialSearch(val) {
      this.dialog = val;
      if (val) {
        setTimeout(() => {
          this.$refs.returnImeiNumber.focus();
          if (this.instantSearchIMEI && this.instantSearchIMEI.imeiNumber) {
            // console.log('we got imei number', this.instantSearchIMEI.imeiNumber)
            this.returnImeiNumber = this.instantSearchIMEI.imeiNumber;
            this.loadImeiNumber();
          }
        }, 100);
      }
    },
  },
  filters: {
    typeFilter(val) {
      let value = "";
      switch (val) {
        case "purchase":
          value = "Purchase";
          break;
        case "invoice":
          value = "Invoice";
          break;
        case "returnToVendor":
          value = "Return To Vendor";
          break;
        case "restock":
          value = "Restock";
          break;
        case "markItSold":
          value = "Mark It Sold";
          break;
        case "refund":
          value = "Refund";
          break;
        case "invoiceDeleted":
          value = "Serial Was Deleted";
          break;
        case "deleted":
          value = "Serial Was Deleted";
          break;
        case "costUpdate":
          value = "Cost Price was updated";
          break;
        case "refundInvoice":
          value = "Invoice Refund";
          break;
        case "statusUpdated":
          value = "Status Updated";
          break;
        default:
          break;
      }
      return value;
    },
  },
  computed: {
    ...mapGetters("layout", ["getOpenSerialSearch", "getInstantSearchIMEI"]),
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    hasPurchaseNameDifference() {
      if (
        !this.product ||
        !this.product.Product ||
        !this.purchaseDetail ||
        !this.purchaseDetail.name
      ) {
        return false;
      }

      const normalize = value => String(value).trim().toLowerCase();
      return (
        normalize(this.purchaseDetail.name) !==
        normalize(this.product.Product.name)
      );
    },
    productLinkMismatch() {
      return Boolean(
        this.product &&
        this.product.Product &&
        this.purchaseDetail &&
        this.purchaseDetail.Product &&
        Number(this.product.Product.id) !== Number(this.purchaseDetail.Product.id)
      );
    },
    purchaseDate() {
      if (!this.purchaseDetail) return null;
      return this.purchaseDetail.Purchase && this.purchaseDetail.Purchase.date
        ? this.purchaseDetail.Purchase.date
        : this.purchaseDetail.createdAt;
    },
    relevantProductNameHistory() {
      if (!this.purchaseDetail || !this.purchaseDetail.name) return [];

      const purchaseName = this.purchaseDetail.name.trim().toLowerCase();
      return this.productNameHistory.filter(history =>
        String(history.changesSummary || "")
          .toLowerCase()
          .includes(purchaseName)
      );
    },
    openSerialSearch: {
      get: function () {
        return this.getOpenSerialSearch;
      },
      set: function (val) {
        this.setOpenSerialSearch(val);
      },
    },
    instantSearchIMEI: {
      get: function () {
        return this.getInstantSearchIMEI;
      },
      set: function (val) {
        this.resetInstantSearchIMEI(val);
      },
    },
  },
  methods: {
    ...mapActions("layout", ["setOpenSerialSearch", "resetInstantSearchIMEI"]),
    openPurchase(item) {
      this.$router.push(`/inventory/purchase/${item.referenceNumber}/edit`);
      this.reset();
      this.close();
    },
    openInvoice(item) {
      this.$router.push(`/invoice/${item.referenceNumber}/edit`);
      this.reset();
      this.close();
    },
    canViewReference(item) {
      return Boolean(
        item.referenceLabel &&
        item.displayReferenceNumber &&
        ["invoice", "refundInvoice", "refund", "purchase", "returnToVendor"].includes(item.type)
      );
    },
    actionMeta(item) {
      const actions = {
        purchase: {
          label: "Purchase",
          icon: "mdi-arrow-down-bold-circle-outline",
          className: "activity-in",
        },
        restock: {
          label: "Restock",
          icon: "mdi-package-down",
          className: "activity-restock",
        },
        refundInvoice: {
          label: "Invoice Refund",
          icon: "mdi-undo-variant",
          className: "activity-in",
        },
        refund: {
          label: "Refund",
          icon: "mdi-undo-variant",
          className: "activity-in",
        },
        invoice: {
          label: "Invoice",
          icon: "mdi-arrow-up-bold-circle-outline",
          className: "activity-out",
        },
        markItSold: {
          label: "Mark It Sold",
          icon: "mdi-cart-arrow-up",
          className: "activity-out",
        },
        returnToVendor: {
          label: "Return To Vendor",
          icon: "mdi-truck-fast-outline",
          className: "activity-danger",
        },
        invoiceDeleted: {
          label: "Serial Was Deleted",
          icon: "mdi-delete-alert-outline",
          className: "activity-danger",
        },
        deleted: {
          label: "Serial Was Deleted",
          icon: "mdi-delete-alert-outline",
          className: "activity-danger",
        },
        costUpdate: {
          label: "Cost Updated",
          icon: "mdi-currency-usd",
          className: "activity-neutral",
        },
        statusUpdated: {
          label: "Status Updated",
          icon: "mdi-swap-horizontal-circle-outline",
          className: "activity-warning",
        },
        productReassigned: {
          label: "Product Link Corrected",
          icon: "mdi-link-variant",
          className: "activity-warning",
        },
      };

      return actions[item.type] || {
        label: item.type || "Activity",
        icon: "mdi-information-outline",
        className: "activity-neutral",
      };
    },
    async submit() {
      if (!this.returnImeiNumber) {
        this.$swal(`${this.label}`, `Item was ${this.label}`, "warning");
        return false;
      }
      this.$swal({
        title: "Are you sure?",
        text: `You want to ${this.label} this. serial number: ${this.returnImeiNumber}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.value) {
          // console.log("this.product.status", this.product.status);
          if (this.product.status === "available") {
            this.markItSold({ imeiNumber: this.returnImeiNumber });
          } else {
            this.restock({ imeiNumber: this.returnImeiNumber });
          }
        }
      });
    },
    async correctProductLink() {
      const targetProduct = this.purchaseDetail && this.purchaseDetail.Product;
      if (!targetProduct) return;

      const confirmation = await this.$swal({
        title: "Restore Serial Product Link?",
        text: `Move serial ${this.returnImeiNumber} from Product #${this.product.Product.localId} to active-purchase Product #${targetProduct.localId}? This correction will be recorded in Serial Search history.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Restore Product Link",
        cancelButtonText: "Cancel",
      });
      if (!confirmation.value) return;

      this.correctingProductLink = true;
      try {
        await purchaseService.reassignSerialProduct({
          imeiNumber: this.returnImeiNumber,
          targetProductLocalId: targetProduct.localId,
        });
        await this.getStatusLog();
        this.$swal({
          title: "Product Link Restored",
          text: `Serial ${this.returnImeiNumber} now belongs to Product #${targetProduct.localId}.`,
          icon: "success",
        });
      } catch (error) {
        this.$swal({
          title: "Unable to Restore Product Link",
          text: error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "The product link could not be corrected.",
          icon: "error",
        });
      } finally {
        this.correctingProductLink = false;
      }
    },
    async openProductEditor() {
      this.selectedTargetProduct = null;
      this.productEditorOpen = true;
      this.loadingProducts = true;
      try {
        const response = await productService.getAll({
          trackSerialNumber: true,
          hideParent: true,
          limit: 10000,
        });
        this.serializedProducts = (response.data || []).filter(item =>
          Number(item.id) !== Number(this.product.Product.id)
        );
      } catch (error) {
        this.productEditorOpen = false;
        this.$swal("Unable to Load Products", "Serialized products could not be loaded.", "error");
      } finally {
        this.loadingProducts = false;
      }
    },
    async changeProduct() {
      if (!this.selectedTargetProduct) return;
      const target = this.selectedTargetProduct;
      const confirmation = await this.$swal({
        title: "Change Product?",
        text: `Move serial ${this.returnImeiNumber} from Product #${this.product.Product.localId} ${this.product.Product.name} to Product #${target.localId} ${target.name}? This change will be recorded in the serial history.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Change Product",
        cancelButtonText: "Cancel",
      });
      if (!confirmation.value) return;

      this.correctingProductLink = true;
      try {
        await purchaseService.reassignSerialProduct({
          imeiNumber: this.returnImeiNumber,
          targetProductId: target.id,
        });
        this.productEditorOpen = false;
        await this.getStatusLog();
        this.$swal("Product Changed", `Serial ${this.returnImeiNumber} now belongs to Product #${target.localId}.`, "success");
      } catch (error) {
        this.$swal({
          title: "Unable to Change Product",
          text: error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "The serial product could not be changed.",
          icon: "error",
        });
      } finally {
        this.correctingProductLink = false;
      }
    },
    async restock(obj) {
      await purchaseService.restock(obj).then(() => {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: `Item was ${this.label}`,
          });
        this.close();
      });
    },
    async markItSold(obj) {
      await purchaseService.markItSold(obj).then(() => {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: `Item was ${this.label}`,
          });
        this.close();
      });
    },
    getStatusLog() {
      return purchaseService
        .searchLog({ imeiNumber: this.returnImeiNumber })
        .then((response) => {
          console.log("response getStatusLog", response);

          if (response.data.serialVault) {
            this.product = response.data.serialVault;
            this.InvoiceDetail = response.data.serialVaultLog;
            this.purchaseDetail = response.data.purchaseDetail;
            this.productNameHistory = response.data.productNameHistory || [];
            if (this.product.status === "available") {
              this.label = "Mark it sold";
              this.btnColor = "orange";
            } else {
              this.label = "Restock";
              this.btnColor = "info";
            }
          } else {
            this.$swal(
              "Not Found",
              "Serial Number you enter is not found",
              "warning"
            );
          }
        })
        .catch((error) => {
          console.log("error", error);
        });
    },
    logClass(item) {
      if (["invoice", "markItSold"].includes(item.type)) return "log-sold";
      if (["purchase", "restock"].includes(item.type)) return "log-stock";
      if (["deleted", "invoiceDeleted", "returnToVendor"].includes(item.type)) return "log-danger";
      return "log-neutral";
    },
    reset() {
      this.InvoiceDetail = [];

      this.product = null;
      this.purchaseDetail = null;
      this.productNameHistory = [];
      this.productEditorOpen = false;
      this.selectedTargetProduct = null;
      this.label = "Restock";
      this.btnColor = "info";
      this.productStatus = null;
    },
    close() {
      this.reset();
      this.returnImeiNumber = null;
      this.openSerialSearch = false;
    },
    view(item) {
      if (["invoice", "refundInvoice", "refund"].includes(item.type)) {
        this.viewInvoice(item.referenceNumber);
      }
      if (item.type === "purchase" || item.type === "returnToVendor") {
        this.viewPurchase(item.referenceNumber, this.returnImeiNumber);
      }
    },
    viewInvoice(id) {
      const hashId = hashids.encode(id);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    viewPurchase(id, search) {
      console.log("id", id, "search", search);
      const hashId = hashids.encode(id);
      const routeData = this.$router.resolve({
        path: `/global/purchase/${hashId}/packingSlip?serialNumber=${search}`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    async loadImeiNumber() {
      this.reset();
      this.textLoading = true;
      await this.getStatusLog();
      // if (this.productStatus) {
      //   await this.getInvoice();
      //   await this.getPurchase();
      //   await this.getRestock();
      // }
      this.textLoading = false;
    },
  },
};
</script>

<style scoped>
.serial-search-dialog{border-radius:22px!important;overflow:hidden}.dialog-header{padding:18px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between}.dialog-title-wrap,.product-main,.product-meta,.user-cell,.history-message{display:flex;align-items:center}.dialog-icon,.product-avatar,.empty-icon{display:flex;align-items:center;justify-content:center;background:#eef4ff}.dialog-icon{width:42px;height:42px;border-radius:13px;margin-right:12px}.dialog-title{font-size:20px;font-weight:800;color:#0f172a}.dialog-subtitle,.cell-subtitle{font-size:12px;color:#64748b}.dialog-body{padding:20px!important;background:#f8fafc}.search-panel{display:flex;gap:10px;padding:14px;background:#fff;border:1px solid #e2e8f0;border-radius:16px}.search-input-wrap{flex:1}.search-button{height:40px!important}.result-wrap{margin-top:16px}.product-card{padding:16px;background:#fff;border:1px solid #e2e8f0;border-radius:18px;display:flex;align-items:center;justify-content:space-between;gap:16px}.product-avatar{width:50px;height:50px;border-radius:15px;margin-right:13px}.eyebrow{font-size:10px;font-weight:800;letter-spacing:.12em;color:#94a3b8}.product-title{font-size:18px;font-weight:800;color:#0f172a}.serial-number{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;color:#64748b;margin-top:3px}.product-meta{gap:10px}.meta-item{display:flex;flex-direction:column;text-align:right}.meta-item span{font-size:10px;color:#94a3b8}.meta-item strong{color:#334155}.result-status{text-transform:capitalize;font-weight:800}.status-dot{width:7px;height:7px;border-radius:50%;background:currentColor;margin-right:6px}.result-status-available{background:#ecfdf3!important;color:#15803d!important}.result-status-sold,.result-status-returned,.result-status-deleted{background:#fff1f2!important;color:#be123c!important}.result-status-onHold,.result-status-repair{background:#fff7ed!important;color:#c2410c!important}.purchase-name-card{margin-top:12px;padding:16px;background:#fffbeb;border:1px solid #fcd34d;border-radius:18px}.purchase-name-heading{display:flex;align-items:center;gap:12px}.purchase-name-icon{width:44px;height:44px;display:flex;align-items:center;justify-content:center;background:#fef3c7;border-radius:13px;flex:none}.purchase-name-title{font-size:17px;font-weight:800;color:#78350f}.name-change-alert{margin-top:14px}.name-audit-wrap{margin-top:12px;padding-top:12px;border-top:1px solid #fde68a}.name-audit-title{font-size:12px;font-weight:800;color:#92400e;margin-bottom:8px}.name-audit-row{padding:8px 0;color:#78350f;font-size:13px}.name-audit-row+.name-audit-row{border-top:1px solid #fde68a}.history-header{padding:18px 2px 10px;display:flex;justify-content:space-between}.section-title{font-size:16px;font-weight:800;color:#0f172a}.history-table-wrap{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden;background:#fff}.history-row:hover{background:#f8fafc}.history-date{color:#2563eb;font-weight:700}.history-message{color:#334155}.log-dot{width:8px;height:8px;border-radius:50%;margin-right:9px;flex:none}.log-sold{background:#f43f5e}.log-stock{background:#22c55e}.log-danger{background:#ef4444}.log-neutral{background:#94a3b8}.user-cell{text-transform:capitalize;color:#475569}.empty-history,.search-empty{text-align:center;color:#64748b}.empty-history{padding:30px}.search-empty{padding:54px 20px}.empty-icon{width:72px;height:72px;border-radius:22px;margin:0 auto 12px}.empty-title{font-size:16px;font-weight:800;color:#334155;margin-bottom:4px}.dialog-actions{padding:12px 20px!important;border-top:1px solid #e2e8f0;background:#fff}@media(max-width:600px){.dialog-body{padding:12px!important}.search-panel{flex-direction:column}.search-button{width:100%}.product-card{align-items:flex-start;flex-direction:column}.product-meta{width:100%;justify-content:space-between}.meta-item{text-align:left}}
.history-reference {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  margin-left: 17px;
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
}

.history-reference:hover {
  text-decoration: underline;
}

.activity-chip {
  border: 1px solid transparent;
  font-size: 10px !important;
  font-weight: 800;
  letter-spacing: 0;
}

.activity-in {
  background: #dcfce7 !important;
  border-color: #86efac !important;
  color: #15803d !important;
}

.activity-restock {
  background: #ccfbf1 !important;
  border-color: #5eead4 !important;
  color: #0f766e !important;
}

.activity-out {
  background: #ffedd5 !important;
  border-color: #fdba74 !important;
  color: #c2410c !important;
}

.activity-danger {
  background: #fee2e2 !important;
  border-color: #fca5a5 !important;
  color: #b91c1c !important;
}

.activity-warning {
  background: #fef3c7 !important;
  border-color: #fcd34d !important;
  color: #92400e !important;
}

.activity-neutral {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
  color: #475569 !important;
}

.product-action-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.edit-product-button {
  background: #fff7db !important;
  border: 1px solid #f4c54f !important;
  color: #a66500 !important;
  font-weight: 800;
  letter-spacing: .04em;
}

.product-editor-dialog {
  border-radius: 22px !important;
  overflow: hidden;
}

.product-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.product-editor-icon {
  background: #eef4ff;
}

.product-editor-body {
  padding: 20px !important;
  background: #f8fafc;
}

.serial-editor-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 13px;
  background: #eef4ff;
}

.serial-editor-number {
  color: #0f172a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 16px;
  font-weight: 800;
}

.product-editor-field {
  margin-top: 18px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.field-label {
  margin-bottom: 9px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.inventory-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 11px 13px;
  color: #475569;
  background: #eff6ff;
  border-radius: 12px;
  font-size: 12px;
}

.product-editor-actions {
  padding: 12px 20px !important;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.product-link-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 14px 16px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 16px;
}

.product-link-title {
  color: #9a3412;
  font-size: 13px;
  font-weight: 800;
}

@media (max-width: 600px) {
  .product-action-stack {
    align-items: flex-end;
  }

  .product-link-card {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
